import React, { CSSProperties, PropsWithChildren, useMemo } from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import cx from 'classnames';

import DragHandleIcon from 'components/shared/Icon/icons/DragHandleIcon';

import Divider from '../Divider';
import styles from './index.module.css';

type LayoutProps = PropsWithChildren<{
  dragIcon?: JSX.Element | null;
  withDivider?: boolean;
  wrapperClassName?: CSSProperties;
  withDnd?: boolean;
}>;

type Props = PropsWithChildren<
  {
    id: string | number;
    disabled?: boolean;
  } & Pick<LayoutProps, 'withDivider' | 'wrapperClassName' | 'withDnd'>
>;

const DefaultDragIcon = () => <DragHandleIcon width="2.4rem" height="2.5rem" />;

export const SortableWrapperLayout = ({
  children,
  dragIcon: initDragIcon,
  withDivider,
  wrapperClassName,
  withDnd,
}: LayoutProps) => {
  const dragIcon = useMemo(() => {
    if (!withDnd) return null;
    if (initDragIcon) return initDragIcon;

    return (
      <div className={cx([styles.dragHandle, styles.dragHandlePadding])}>
        <DefaultDragIcon />
      </div>
    );
  }, []);

  return (
    <div className={cx(styles.wrapper, wrapperClassName)}>
      <div className={cx(styles.container)}>
        {dragIcon}

        <div style={{ width: '100%' }}>{children}</div>
      </div>

      {withDivider && <Divider isMarginSm />}
    </div>
  );
};

export const SortableWrapper = (props: Props) => {
  const { id, children, disabled } = props;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    userSelect: 'none',
    pointerEvents: isDragging ? 'none' : undefined,
    opacity: isDragging ? 0.5 : undefined,
  };

  const dragIcon = useMemo(() => {
    return (
      <div
        className={cx([
          styles.dragHandle,
          styles.dragHandlePadding,
          isDragging && styles.dragHandleDragging,
          disabled && styles.dragDisabled,
        ])}
        style={{
          touchAction: 'none',
          userSelect: 'none',
          display: 'flex',
        }}
        {...listeners}
      >
        <DefaultDragIcon />
      </div>
    );
  }, []);

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <SortableWrapperLayout dragIcon={dragIcon} {...props}>
        {children}
      </SortableWrapperLayout>
    </div>
  );
};
